import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React from 'react';

import {MoleculeCanonicalSmilesQuery} from 'chemistry/molecules/tabs/__generated/MoleculeCanonicalSmilesQuery';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import getTestProps from 'test/lib/cypress/getTestProps';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {TEXT_FIELD_BACKGROUND_COLOR, TEXT_FIELD_BORDER} from '../MoleculeConstants';

export const MOLECULE_CANONICAL_SMILES_QUERY = gql`
  query MoleculeCanonicalSmilesQuery($moleculeId: StrictString!, $moleculeVersionId: StrictString!)
  @skipAudit
  @engComponent(name: CHEMISTRY) {
    molecule(id: $moleculeId) {
      ... on MoleculeNode {
        id
        version(versionId: $moleculeVersionId) {
          id
          chemicalStructure {
            id
            canonicalSmiles
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface MoleculeCanonicalSmilesProps {
  moleculeId: string;
  moleculeVersionId: string;
}

export default function MoleculeCanonicalSmiles(props: MoleculeCanonicalSmilesProps): JSX.Element | null {
  return (
    <BenchlingQuery
      query={MOLECULE_CANONICAL_SMILES_QUERY}
      variables={{moleculeId: props.moleculeId, moleculeVersionId: props.moleculeVersionId}}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {(data: MoleculeCanonicalSmilesQuery) => {
        if (isInaccessible(data.molecule)) {
          return (
            <div>You do not have access to view this molecule. Please ask your administrator for access.</div>
          );
        }
        const canonicalSmiles = data.molecule.version
          ? data.molecule.version.chemicalStructure.canonicalSmiles
          : 'Chemical structure for this version not found';
        return (
          <div>
            <div className={css(styles.fieldHeader)}>Canonical SMILES</div>
            <div
              className={css(styles.fieldValue)}
              {...getTestProps({component: 'MoleculeChemicalStructure', element: 'canonicalSmiles'})}
            >
              {canonicalSmiles}
            </div>
          </div>
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  fieldHeader: {
    fontWeight: 'bold',
    margin: '4px 0px',
    paddingTop: '10px',
  },
  fieldValue: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '6px 12px',
    background: TEXT_FIELD_BACKGROUND_COLOR,
    border: TEXT_FIELD_BORDER,
    boxSizing: 'border-box',
    borderRadius: '4px',
    overflowWrap: 'anywhere',
  },
});
